// ─── Types ────────────────────────────────────────────────────────────────────

export interface Service {
  id: string;
  name: string;
  description: string;
  estimatedTime: string;
  icon: string;
}

export interface Testimonial {
  id: string;
  name: string;
  car: string;
  rating: number;
  quote: string;
  date: string;
}

export interface TimeSlot {
  id: string;
  label: string;
  available: boolean;
}

// ─── Services ─────────────────────────────────────────────────────────────────

export const SERVICES: Service[] = [
  {
    id: 'collision',
    name: 'Collision Repair',
    description: 'Full structural and cosmetic restoration after accidents, backed by our lifetime warranty.',
    estimatedTime: '3–7 days',
    icon: '🔧',
  },
  {
    id: 'dent',
    name: 'Dent Removal',
    description: 'Paintless dent repair for minor dings and dents — no filler, no repainting needed.',
    estimatedTime: '1–2 days',
    icon: '✨',
  },
  {
    id: 'paint',
    name: 'Auto Painting',
    description: 'Color-matched paint services using OEM-grade materials for a flawless, factory finish.',
    estimatedTime: '2–4 days',
    icon: '🎨',
  },
  {
    id: 'frame',
    name: 'Frame Straightening',
    description: 'Computer-aided frame alignment to restore your vehicle to factory specifications.',
    estimatedTime: '2–5 days',
    icon: '📐',
  },
  {
    id: 'glass',
    name: 'Glass Replacement',
    description: 'Windshield and auto glass repair and replacement using OEM or equivalent glass.',
    estimatedTime: 'Same day',
    icon: '🔲',
  },
  {
    id: 'detail',
    name: 'Auto Detailing',
    description: 'Interior and exterior detailing to restore your vehicle to showroom condition.',
    estimatedTime: '4–8 hours',
    icon: '💎',
  },
];

// ─── Testimonials ─────────────────────────────────────────────────────────────

export const TESTIMONIALS: Testimonial[] = [
  {
    id: 't1',
    name: 'Marcus T.',
    car: '2021 Honda Accord',
    rating: 5,
    quote: 'After my fender bender, Apex had my car looking brand new. The color match was perfect and they finished two days early. Couldn\'t be happier.',
    date: 'March 2025',
  },
  {
    id: 't2',
    name: 'Priya S.',
    car: '2019 Toyota Camry',
    rating: 5,
    quote: 'Professional, fast, and honest. They gave me a free estimate and stuck to the quote. My car looks like the accident never happened.',
    date: 'February 2025',
  },
  {
    id: 't3',
    name: 'James W.',
    car: '2022 Ford F-150',
    rating: 5,
    quote: 'Took my truck in for frame work after a collision. The team was transparent every step of the way. Exceptional quality and customer service.',
    date: 'January 2025',
  },
  {
    id: 't4',
    name: 'Lauren M.',
    car: '2020 Subaru Outback',
    rating: 5,
    quote: 'Best auto body shop I\'ve ever used. Handled everything with my insurance, kept me updated daily, and the result was flawless.',
    date: 'December 2024',
  },
  {
    id: 't5',
    name: 'Derek O.',
    car: '2023 BMW 3 Series',
    rating: 5,
    quote: 'I was nervous about trusting anyone with my BMW, but Apex exceeded every expectation. The paintless dent removal saved me thousands.',
    date: 'November 2024',
  },
];

// ─── Date Helpers ─────────────────────────────────────────────────────────────

export function getNextDays(count: number): string[] {
  const days: string[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let i = 1; i <= count; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    // Skip Sundays (0)
    if (d.getDay() !== 0) {
      days.push(d.toISOString().split('T')[0]);
    }
  }
  return days;
}

export function formatDate(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

export function formatDateLong(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

// ─── Time Slot Generator ──────────────────────────────────────────────────────

const BASE_SLOTS = [
  '8:00 AM', '8:30 AM', '9:00 AM', '9:30 AM',
  '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  '12:00 PM', '1:00 PM', '1:30 PM', '2:00 PM',
  '2:30 PM', '3:00 PM', '3:30 PM', '4:00 PM',
];

// Deterministic pseudo-random availability based on date string
function seededRandom(seed: string, index: number): number {
  let hash = 0;
  const str = seed + String(index);
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash) / 2147483647;
}

export function generateTimeSlots(date: string): TimeSlot[] {
  return BASE_SLOTS.map((label, i) => ({
    id: `slot-${i}`,
    label,
    available: seededRandom(date, i) > 0.3,
  }));
}

// ─── Confirmation Code ────────────────────────────────────────────────────────

export function generateConfirmationCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = 'APX-';
  for (let i = 0; i < 4; i++) {
    code += chars[Math.floor(Math.random() * chars.length)];
  }
  return code;
}

// ─── Service Lookup ───────────────────────────────────────────────────────────

export function getServiceById(id: string): Service | undefined {
  return SERVICES.find(s => s.id === id);
}
